import { useState, useMemo, useEffect } from 'react'
import { Link } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { Plus, Search, Pencil, Trash2, Users, X, ChevronDown, ChevronRight } from 'lucide-react'

import { apiClient } from '../services/api'
import LoadingSpinner from '../components/LoadingSpinner'
import { useOrganization } from '../contexts/OrganizationContext'

interface Permission {
  id: string
  user_id: string
  user_display_name: string | null
  user_email: string | null
  valid_from: string
  valid_to: string | null
  is_active: boolean
}

interface CostCenter {
  id: string
  code: string
  name: string
  description: string | null
  is_deleted: boolean
  show_on_ambulance_order: boolean
  ahola_uuid: string | null
  ahola_external_id: string | null
  created_by: string | null
  updated_by: string | null
  organization_id: string
  created_at: string
  updated_at: string | null
  deleted_at: string | null
  permissions?: Permission[]
}

interface CostCenterWithDetails extends CostCenter {
  created_by_name: string | null
  updated_by_name: string | null
  permissions: Permission[]
}

interface CostCenterListResponse {
  data: CostCenter[]
  pagination: {
    page: number
    page_size: number
    total_items: number
    total_pages: number
  }
}

interface CreateCostCenterData {
  code: string
  name: string
  description?: string
  show_on_ambulance_order?: boolean
  ahola_uuid?: string
  ahola_external_id?: string
}

interface UpdateCostCenterData {
  code?: string
  name?: string
  description?: string
  show_on_ambulance_order?: boolean
  ahola_uuid?: string
  ahola_external_id?: string
}

// Format datetime for display
function formatDateTime(dateStr: string | null): string {
  if (!dateStr) return '-'
  const date = new Date(dateStr)
  return date.toLocaleString('fi-FI', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  })
}

// Cost Center Row component with expandable permissions
function CostCenterRow({
  cc,
  onEdit,
  handleDelete,
  handleToggleAmbulance,
  expandedRows,
  setExpandedRows,
  t,
  tc,
}: {
  cc: CostCenter
  onEdit: (cc: CostCenter) => void
  handleDelete: (cc: CostCenter) => void
  handleToggleAmbulance: (id: string, value: boolean) => void
  expandedRows: Set<string>
  setExpandedRows: React.Dispatch<React.SetStateAction<Set<string>>>
  t: (key: string, opts?: Record<string, unknown>) => string
  tc: (key: string) => string
}) {
  const isExpanded = expandedRows.has(cc.id)

  // Fetch permissions for this cost center when expanded
  const { data: ccWithPerms } = useQuery<CostCenterWithDetails>({
    queryKey: ['cost-center', cc.id],
    queryFn: () => apiClient.get(`/cost-centers/${cc.id}`),
    enabled: isExpanded,
  })

  const toggleExpand = () => {
    setExpandedRows((prev) => {
      const newSet = new Set(prev)
      if (newSet.has(cc.id)) {
        newSet.delete(cc.id)
      } else {
        newSet.add(cc.id)
      }
      return newSet
    })
  }

  const activePermissions = ccWithPerms?.permissions?.filter((p) => p.is_active) || []

  return (
    <>
      <tr className={`hover:bg-gray-50 ${cc.is_deleted ? 'opacity-50' : ''}`}>
        <td className="px-6 py-4 whitespace-nowrap text-sm">
          <button
            onClick={toggleExpand}
            className="flex items-center gap-2 text-gray-600 hover:text-gray-900"
          >
            {isExpanded ? (
              <ChevronDown className="w-4 h-4" />
            ) : (
              <ChevronRight className="w-4 h-4" />
            )}
            <span className="font-mono font-medium">{cc.code}</span>
          </button>
        </td>
        <td className="px-6 py-4 whitespace-nowrap text-sm">{cc.name}</td>
        <td className="px-6 py-4 text-sm text-gray-600 max-w-xs truncate">
          {cc.description || '-'}
        </td>
        <td className="px-6 py-4 whitespace-nowrap text-center">
          <input
            type="checkbox"
            checked={cc.show_on_ambulance_order}
            onChange={(e) => handleToggleAmbulance(cc.id, e.target.checked)}
            disabled={cc.is_deleted}
            className="h-4 w-4 text-blue-600 rounded border-gray-300 focus:ring-blue-500 disabled:opacity-50"
            title={t('costCenters.showOnAmbulanceOrder')}
          />
        </td>
        <td className="px-6 py-4 whitespace-nowrap">
          {cc.is_deleted ? (
            <span className="px-2 py-1 text-xs font-medium bg-red-100 text-red-800 rounded-full">
              {t('costCenters.deleted')}
            </span>
          ) : (
            <span className="px-2 py-1 text-xs font-medium bg-green-100 text-green-800 rounded-full">
              {t('costCenters.active')}
            </span>
          )}
        </td>
        <td className="px-6 py-4 whitespace-nowrap text-right">
          <div className="flex items-center justify-end gap-2">
            <Link
              to={`/cost-centers/${cc.id}/permissions`}
              className="p-1 text-blue-600 hover:bg-blue-50 rounded"
              title={t('costCenters.managePermissions')}
            >
              <Users className="w-5 h-5" />
            </Link>
            {!cc.is_deleted && (
              <>
                <button
                  onClick={() => onEdit(cc)}
                  className="p-1 text-gray-600 hover:bg-gray-100 rounded"
                  title={tc('common.edit')}
                >
                  <Pencil className="w-5 h-5" />
                </button>
                <button
                  onClick={() => handleDelete(cc)}
                  className="p-1 text-red-600 hover:bg-red-50 rounded"
                  title={tc('common.delete')}
                >
                  <Trash2 className="w-5 h-5" />
                </button>
              </>
            )}
          </div>
        </td>
      </tr>
      {/* Expanded row showing users with permissions */}
      {isExpanded && (
        <tr className="bg-gray-50">
          <td colSpan={6} className="px-6 py-3">
            <div className="pl-6">
              <p className="text-xs font-medium text-gray-500 uppercase mb-2">
                {t('costCenters.usersWithAccess')}
              </p>
              {activePermissions.length === 0 ? (
                <p className="text-sm text-gray-500 italic">{t('costCenters.noUsersWithAccess')}</p>
              ) : (
                <div className="flex flex-wrap gap-2">
                  {activePermissions.map((perm) => (
                    <span
                      key={perm.id}
                      className="inline-flex items-center px-2 py-1 bg-white border rounded-full text-sm"
                    >
                      {perm.user_display_name || perm.user_email}
                      {perm.valid_to && (
                        <span className="ml-1 text-xs text-gray-400">
                          ({t('costCenters.permissions.validTo')}: {new Date(perm.valid_to).toLocaleDateString()})
                        </span>
                      )}
                    </span>
                  ))}
                </div>
              )}
            </div>
          </td>
        </tr>
      )}
    </>
  )
}

export default function CostCentersPage() {
  const { t } = useTranslation('admin')
  const { t: tc } = useTranslation('common')
  const queryClient = useQueryClient()

  const [search, setSearch] = useState('')
  const [userNameFilter, setUserNameFilter] = useState('')
  const [debouncedUserNameFilter, setDebouncedUserNameFilter] = useState('')
  const [includeDeleted, setIncludeDeleted] = useState(false)
  const [showCreateModal, setShowCreateModal] = useState(false)
  const [showEditModal, setShowEditModal] = useState(false)
  const [editingCostCenter, setEditingCostCenter] = useState<CostCenterWithDetails | null>(null)
  const [expandedRows, setExpandedRows] = useState<Set<string>>(new Set())

  // Use global organization context
  const { selectedOrgId } = useOrganization()

  // Debounce user name filter (500ms)
  useEffect(() => {
    const timer = setTimeout(() => setDebouncedUserNameFilter(userNameFilter), 500)
    return () => clearTimeout(timer)
  }, [userNameFilter])

  // Form state for new cost center
  const [newCode, setNewCode] = useState('')
  const [newName, setNewName] = useState('')
  const [newDescription, setNewDescription] = useState('')
  const [newShowOnAmbulance, setNewShowOnAmbulance] = useState(false)
  const [newAholaUuid, setNewAholaUuid] = useState('')
  const [newAholaExternalId, setNewAholaExternalId] = useState('')

  // Form state for editing cost center
  const [editCode, setEditCode] = useState('')
  const [editName, setEditName] = useState('')
  const [editDescription, setEditDescription] = useState('')
  const [editShowOnAmbulance, setEditShowOnAmbulance] = useState(false)
  const [editAholaUuid, setEditAholaUuid] = useState('')
  const [editAholaExternalId, setEditAholaExternalId] = useState('')

  // Fetch all cost centers at once — search is done client-side
  const { data, isLoading, error } = useQuery<CostCenterListResponse>({
    queryKey: ['cost-centers', debouncedUserNameFilter, includeDeleted, selectedOrgId],
    queryFn: () => {
      const params = new URLSearchParams({
        page: '1',
        page_size: '10000',
        include_deleted: includeDeleted.toString(),
      })
      if (debouncedUserNameFilter) params.append('user_name', debouncedUserNameFilter)
      if (selectedOrgId) params.append('organization_id', selectedOrgId)
      return apiClient.get(`/cost-centers?${params}`)
    },
  })

  // Client-side filtering by code/name search
  const filteredData = useMemo(() => {
    if (!data?.data) return []
    if (!search.trim()) return data.data
    const term = search.toLowerCase()
    return data.data.filter(
      (cc) =>
        cc.code.toLowerCase().includes(term) ||
        cc.name.toLowerCase().includes(term)
    )
  }, [data?.data, search])

  const [createError, setCreateError] = useState<string | null>(null)
  const createMutation = useMutation({
    mutationFn: (data: CreateCostCenterData) => {
      const url = selectedOrgId
        ? `/cost-centers?organization_id=${selectedOrgId}`
        : '/cost-centers'
      return apiClient.post(url, data)
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['cost-centers'] })
      setShowCreateModal(false)
      setCreateError(null)
      setNewCode('')
      setNewName('')
      setNewDescription('')
      setNewShowOnAmbulance(false)
      setNewAholaUuid('')
      setNewAholaExternalId('')
    },
    onError: (err: Error) => {
      setCreateError(err.message || 'Kustannuspaikan luonti epäonnistui')
    },
  })

  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdateCostCenterData }) =>
      apiClient.patch(`/cost-centers/${id}`, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['cost-centers'] })
      queryClient.invalidateQueries({ queryKey: ['cost-center'] })
      setShowEditModal(false)
      setEditingCostCenter(null)
    },
  })

  const deleteMutation = useMutation({
    mutationFn: (id: string) => apiClient.delete(`/cost-centers/${id}`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['cost-centers'] })
    },
  })

  const toggleAmbulanceMutation = useMutation({
    mutationFn: ({ id, show_on_ambulance_order }: { id: string; show_on_ambulance_order: boolean }) =>
      apiClient.patch(`/cost-centers/${id}`, { show_on_ambulance_order }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['cost-centers'] })
    },
  })

  const handleToggleAmbulance = (id: string, value: boolean) => {
    toggleAmbulanceMutation.mutate({ id, show_on_ambulance_order: value })
  }

  const handleCreate = (e: React.FormEvent) => {
    e.preventDefault()
    createMutation.mutate({
      code: newCode,
      name: newName,
      description: newDescription || undefined,
      show_on_ambulance_order: newShowOnAmbulance,
      ahola_uuid: newAholaUuid || undefined,
      ahola_external_id: newAholaExternalId || undefined,
    })
  }

  const handleStartEdit = async (cc: CostCenter) => {
    // Fetch the cost center with details
    try {
      const details = await apiClient.get<CostCenterWithDetails>(`/cost-centers/${cc.id}`)
      setEditingCostCenter(details)
      setEditCode(details.code)
      setEditName(details.name)
      setEditDescription(details.description || '')
      setEditShowOnAmbulance(details.show_on_ambulance_order)
      setEditAholaUuid(details.ahola_uuid || '')
      setEditAholaExternalId(details.ahola_external_id || '')
      setShowEditModal(true)
    } catch (err) {
      console.error('Failed to fetch cost center details:', err)
    }
  }

  const handleSaveEdit = (e: React.FormEvent) => {
    e.preventDefault()
    if (!editingCostCenter) return
    updateMutation.mutate({
      id: editingCostCenter.id,
      data: {
        code: editCode,
        name: editName,
        description: editDescription || undefined,
        show_on_ambulance_order: editShowOnAmbulance,
        ahola_uuid: editAholaUuid || undefined,
        ahola_external_id: editAholaExternalId || undefined,
      },
    })
  }

  const handleDelete = (cc: CostCenter) => {
    if (window.confirm(t('costCenters.confirmDelete', { name: cc.name }))) {
      deleteMutation.mutate(cc.id)
    }
  }

  if (isLoading) {
    return <LoadingSpinner />
  }

  if (error) {
    return (
      <div className="text-center py-12">
        <p className="text-red-600">{tc('errors.generic')}</p>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">{t('costCenters.title')}</h1>
          <p className="text-gray-600">{t('costCenters.description')}</p>
        </div>
        <button
          onClick={() => setShowCreateModal(true)}
          className="inline-flex items-center px-4 py-2 bg-black text-white rounded-lg hover:bg-gray-800 transition-colors"
        >
          <Plus className="w-5 h-5 mr-2" />
          {t('costCenters.add')}
        </button>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-xl shadow-sm border p-4">
        <div className="flex flex-wrap gap-4 items-center">
          <div className="relative flex-1 min-w-[200px]">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
            <input
              type="text"
              placeholder={t('costCenters.searchPlaceholder')}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
            />
          </div>
          <div className="relative min-w-[200px]">
            <Users className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
            <input
              type="text"
              placeholder={t('costCenters.searchByUser')}
              value={userNameFilter}
              onChange={(e) => setUserNameFilter(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
            />
          </div>
          <label className="flex items-center gap-2 text-sm text-gray-600">
            <input
              type="checkbox"
              checked={includeDeleted}
              onChange={(e) => setIncludeDeleted(e.target.checked)}
              className="rounded border-gray-300"
            />
            {t('costCenters.showDeleted')}
          </label>
        </div>
      </div>

      {/* Table */}
      <div className="bg-white rounded-xl shadow-sm border overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50 border-b">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                  {t('costCenters.code')}
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                  {t('costCenters.name')}
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                  {t('costCenters.descriptionLabel')}
                </th>
                <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase">
                  {t('costCenters.ambulance')}
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                  {t('costCenters.status')}
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">
                  {t('costCenters.actions')}
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {filteredData.length === 0 ? (
                <tr>
                  <td colSpan={6} className="px-6 py-12 text-center text-gray-500">
                    {t('costCenters.noResults')}
                  </td>
                </tr>
              ) : (
                filteredData.map((cc) => (
                  <CostCenterRow
                    key={cc.id}
                    cc={cc}
                    onEdit={handleStartEdit}
                    handleDelete={handleDelete}
                    handleToggleAmbulance={handleToggleAmbulance}
                    expandedRows={expandedRows}
                    setExpandedRows={setExpandedRows}
                    t={t}
                    tc={tc}
                  />
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Result count */}
        {data && (
          <div className="px-6 py-3 border-t">
            <p className="text-sm text-gray-600">
              {filteredData.length} / {data.pagination.total_items} {t('costCenters.title').toLowerCase()}
            </p>
          </div>
        )}
      </div>

      {/* Create Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-md mx-4">
            <div className="px-6 py-4 border-b flex items-center justify-between">
              <h2 className="text-lg font-semibold">{t('costCenters.createTitle')}</h2>
              <button onClick={() => setShowCreateModal(false)} className="text-gray-400 hover:text-gray-600">
                <X className="w-5 h-5" />
              </button>
            </div>
            <form onSubmit={handleCreate} className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {t('costCenters.code')} *
                </label>
                <input
                  type="text"
                  value={newCode}
                  onChange={(e) => setNewCode(e.target.value)}
                  required
                  maxLength={50}
                  className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                  placeholder="e.g., 1001"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {t('costCenters.name')} *
                </label>
                <input
                  type="text"
                  value={newName}
                  onChange={(e) => setNewName(e.target.value)}
                  required
                  maxLength={255}
                  className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                  placeholder="e.g., Hallinto"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {t('costCenters.descriptionLabel')}
                </label>
                <textarea
                  value={newDescription}
                  onChange={(e) => setNewDescription(e.target.value)}
                  rows={3}
                  className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                />
              </div>
              <div>
                <label className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    checked={newShowOnAmbulance}
                    onChange={(e) => setNewShowOnAmbulance(e.target.checked)}
                    className="h-4 w-4 text-blue-600 rounded border-gray-300 focus:ring-blue-500"
                  />
                  <span className="text-sm font-medium text-gray-700">
                    {t('costCenters.showOnAmbulanceOrder')}
                  </span>
                </label>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {t('costCenters.aholaUuid')}
                </label>
                <input
                  type="text"
                  value={newAholaUuid}
                  onChange={(e) => setNewAholaUuid(e.target.value)}
                  maxLength={36}
                  className="w-full px-3 py-2 border rounded-lg font-mono text-sm focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                  placeholder="xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {t('costCenters.aholaExternalId')}
                </label>
                <input
                  type="text"
                  value={newAholaExternalId}
                  onChange={(e) => setNewAholaExternalId(e.target.value)}
                  maxLength={500}
                  className="w-full px-3 py-2 border rounded-lg text-sm focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                />
              </div>
              {createError && (
                <div className="bg-red-50 text-red-700 text-sm px-3 py-2 rounded">
                  {createError}
                </div>
              )}
              <div className="flex justify-end gap-3 pt-4">
                <button
                  type="button"
                  onClick={() => setShowCreateModal(false)}
                  className="px-4 py-2 border rounded-lg hover:bg-gray-50"
                >
                  {tc('common.cancel')}
                </button>
                <button
                  type="submit"
                  disabled={createMutation.isPending}
                  className="px-4 py-2 bg-black text-white rounded-lg hover:bg-gray-800 disabled:opacity-50"
                >
                  {createMutation.isPending ? tc('common.saving') : t('costCenters.create')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Edit Modal */}
      {showEditModal && editingCostCenter && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-md mx-4">
            <div className="px-6 py-4 border-b flex items-center justify-between">
              <h2 className="text-lg font-semibold">{t('costCenters.editTitle')}</h2>
              <button
                onClick={() => {
                  setShowEditModal(false)
                  setEditingCostCenter(null)
                }}
                className="text-gray-400 hover:text-gray-600"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            <form onSubmit={handleSaveEdit} className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {t('costCenters.code')} *
                </label>
                <input
                  type="text"
                  value={editCode}
                  onChange={(e) => setEditCode(e.target.value)}
                  required
                  maxLength={50}
                  className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {t('costCenters.name')} *
                </label>
                <input
                  type="text"
                  value={editName}
                  onChange={(e) => setEditName(e.target.value)}
                  required
                  maxLength={255}
                  className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {t('costCenters.descriptionLabel')}
                </label>
                <textarea
                  value={editDescription}
                  onChange={(e) => setEditDescription(e.target.value)}
                  rows={3}
                  className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                />
              </div>
              <div>
                <label className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    checked={editShowOnAmbulance}
                    onChange={(e) => setEditShowOnAmbulance(e.target.checked)}
                    className="h-4 w-4 text-blue-600 rounded border-gray-300 focus:ring-blue-500"
                  />
                  <span className="text-sm font-medium text-gray-700">
                    {t('costCenters.showOnAmbulanceOrder')}
                  </span>
                </label>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {t('costCenters.aholaUuid')}
                </label>
                <input
                  type="text"
                  value={editAholaUuid}
                  onChange={(e) => setEditAholaUuid(e.target.value)}
                  maxLength={36}
                  className="w-full px-3 py-2 border rounded-lg font-mono text-sm focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                  placeholder="xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {t('costCenters.aholaExternalId')}
                </label>
                <input
                  type="text"
                  value={editAholaExternalId}
                  onChange={(e) => setEditAholaExternalId(e.target.value)}
                  maxLength={500}
                  className="w-full px-3 py-2 border rounded-lg text-sm focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                />
              </div>

              {/* Metadata section */}
              <div className="pt-4 border-t text-sm text-gray-500 space-y-1">
                <p>
                  {t('costCenters.created')}: {formatDateTime(editingCostCenter.created_at)}
                  {editingCostCenter.created_by_name && ` (${editingCostCenter.created_by_name})`}
                </p>
                {editingCostCenter.updated_at && (
                  <p>
                    {t('costCenters.updated')}: {formatDateTime(editingCostCenter.updated_at)}
                    {editingCostCenter.updated_by_name && ` (${editingCostCenter.updated_by_name})`}
                  </p>
                )}
              </div>

              <div className="flex justify-end gap-3 pt-4">
                <button
                  type="button"
                  onClick={() => {
                    setShowEditModal(false)
                    setEditingCostCenter(null)
                  }}
                  className="px-4 py-2 border rounded-lg hover:bg-gray-50"
                >
                  {tc('common.cancel')}
                </button>
                <button
                  type="submit"
                  disabled={updateMutation.isPending}
                  className="px-4 py-2 bg-black text-white rounded-lg hover:bg-gray-800 disabled:opacity-50"
                >
                  {updateMutation.isPending ? tc('common.saving') : tc('common.save')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  )
}
